import { Star, Plus } from "lucide-react";
import { type ReactNode, useCallback, useRef, useState } from "react";
import { Button } from "../../../../../components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../../../components/ui/tooltip";
import { LoadingSpinner } from "../../../../../components/ui/loadingSpinner/loadingSpinner";
import FileViewer, {
  type ViewerBoundingBox,
} from "../../../../../pages/extraction/viewExtraction/FileViewer";
import { LinkingData } from "../../../../../pages/extraction/viewExtraction/PdfViewer";
import { Citation } from "../../../../../models/audit/citation";
import { type BoundingBoxState } from "./types";
import { useAudit } from "../../../../../contexts/auditContext";

interface DocumentViewerPanelProps {
  showDocumentViewer: boolean;
  currentBoundingBox: BoundingBoxState | undefined;
  viewerBoundingBoxes: ViewerBoundingBox[] | undefined;
  selectedBoundingBoxId: string | undefined;
  selectedBoundingBoxIndex: number | undefined;
  currentSelectionValue: string | undefined;
  allCitations: Citation[];
  onLinkingComplete: (linkingData: LinkingData) => Promise<void>;
  onDelete: (params: { id?: string; index: number }) => Promise<void>;
  onSelect: (params: { id?: string }) => void;
  onToggleKeyCitation: () => Promise<void>;
  onAddCitation?: () => void;
  /** Gate citation linking/deleting in the viewer (default true). Support-cell
   * fold-outs render the viewer read-only. */
  allowEditing?: boolean;
  /** Show the "Include in WP" key-citation toggle (default true). Not
   * meaningful for support cells. */
  allowKeyToggle?: boolean;
  /** SOX tickmark callouts over PDFs. When `onToggleTickmarks` is provided, a
   * toggle chip renders in the toolbar (PDF documents only). */
  showTickmarks?: boolean;
  onToggleTickmarks?: () => void;
  /** When the active evidence has both a rendered screenshot and a live source
   * document, a segmented control to switch the viewer between them. Null hides
   * it. */
  viewToggle?: {
    mode: "screenshot" | "source";
    sourceLabel: string;
    onSelect: (mode: "screenshot" | "source") => void;
  } | null;
  /** Names the document in the toolbar in place of its file name. */
  documentLabel?: string;
  /** Steps between citations that may sit on different documents; rendered
   * last in the toolbar, against its right edge. */
  citationPager?: ReactNode;
  /**
   * Draws an image document at the pane's width, scrolling if it is taller,
   * with no zoom, reset or fullscreen controls. For a picture meant to be read
   * top to bottom rather than inspected; other documents ignore it.
   */
  fitImageToWidth?: boolean;
}

const IMAGE_FILE_TYPES = new Set(["png", "jpeg", "webp"]);

export function DocumentViewerPanel({
  showDocumentViewer,
  currentBoundingBox,
  viewerBoundingBoxes,
  selectedBoundingBoxId,
  selectedBoundingBoxIndex,
  currentSelectionValue,
  allCitations,
  onLinkingComplete,
  onDelete,
  onSelect,
  onToggleKeyCitation,
  onAddCitation,
  allowEditing = true,
  allowKeyToggle = true,
  showTickmarks = false,
  onToggleTickmarks,
  viewToggle,
  documentLabel,
  citationPager,
  fitImageToWidth = false,
}: DocumentViewerPanelProps) {
  const { selectedDocument, currentSelectedDocumentUrl, controlSession } =
    useAudit();

  // When the viewer pane is narrow, collapse the toolbar action buttons to just
  // their icons so they don't overflow off-screen. Measured (not a viewport
  // breakpoint) since the pane is independently resizable. Callback ref so the
  // observer (re)attaches across the component's early returns.
  const [compactActions, setCompactActions] = useState(false);
  const observerRef = useRef<ResizeObserver | null>(null);
  const measureRef = useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    if (!node) {
      observerRef.current = null;
      return;
    }
    const update = () => setCompactActions(node.clientWidth < 500);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    observerRef.current = observer;
  }, []);

  const isSessionComplete = controlSession?.status === "complete";
  const canEdit = allowEditing && !isSessionComplete;
  // `SelectedDocument` carries the type in two places: on the fetched API
  // document and, for callers that select a document by id alone, at the top
  // level. Resolve both the way `auditContext` does, since defaulting to "pdf"
  // on a PNG routes it to the PDF viewer, which fetches the bytes over XHR and
  // fails where the image viewer's plain `<img>` would have rendered it.
  const fileType =
    selectedDocument?.fetchableDocument?.fileType ||
    selectedDocument?.fileType ||
    "pdf";
  const isPdf = fileType === "pdf";
  // Tickmarks only mean something when the shown document has citation boxes to
  // annotate — e.g. Excel screenshots carry no bounding boxes, so hide the toggle.
  const hasBoundingBoxes = (viewerBoundingBoxes?.length ?? 0) > 0;

  if (!showDocumentViewer || !selectedDocument) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-font-secondary">
        Select an evidence item to preview.
      </div>
    );
  }

  if (!currentSelectedDocumentUrl) {
    return (
      <div className="h-full flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const isKeyCitation = allCitations.find(
    (c) => c.id === currentBoundingBox?.citationId,
  )?.isKey;

  return (
    <div ref={measureRef} className="h-full w-full flex flex-col min-h-0">
      <div className="flex items-center justify-between gap-2 mx-4 my-2">
        {/* A label names what the document shows rather than the file, so it
            is set as a title instead of in the file-name chip. */}
        {documentLabel ? (
          <div className="min-w-0 truncate text-sm text-stone-900">
            {documentLabel}
          </div>
        ) : (
          <div className="min-w-0 px-3 py-1.5 text-xs bg-stone-50 border border-stone-200 truncate">
            {selectedDocument?.fileName ||
              selectedDocument?.fetchableDocument?.fileName ||
              ""}
          </div>
        )}
        <div className="flex items-center gap-2 shrink-0">
          {onToggleTickmarks && isPdf && hasBoundingBoxes && (
            <button
              type="button"
              onClick={onToggleTickmarks}
              className={`border px-2 py-1 text-xs transition-colors ${
                showTickmarks
                  ? "border-red-300 bg-red-50 text-red-700"
                  : "border-stone-200 text-stone-500 hover:text-stone-700"
              }`}
            >
              ✓ tickmarks
            </button>
          )}
          {viewToggle && (
            <div className="flex items-center border border-stone-200 p-0.5 text-xs">
              <button
                type="button"
                onClick={() => viewToggle.onSelect("screenshot")}
                className={`px-2 py-1 transition-colors ${
                  viewToggle.mode === "screenshot"
                    ? "bg-stone-200 font-medium text-stone-900"
                    : "text-stone-500 hover:text-stone-700"
                }`}
              >
                Screenshot
              </button>
              <button
                type="button"
                onClick={() => viewToggle.onSelect("source")}
                className={`px-2 py-1 transition-colors ${
                  viewToggle.mode === "source"
                    ? "bg-stone-200 font-medium text-stone-900"
                    : "text-stone-500 hover:text-stone-700"
                }`}
              >
                {viewToggle.sourceLabel}
              </button>
            </div>
          )}
          {onAddCitation && !isSessionComplete && (
            <Button
              variant="outline"
              size="sm"
              onClick={onAddCitation}
              title="Add Citation"
              className={`flex items-center h-7 text-xs ${
                compactActions ? "px-2" : "gap-2"
              }`}
            >
              <Plus className="h-4 w-4" />
              {!compactActions && "Add Citation"}
            </Button>
          )}
          {allowKeyToggle &&
            currentBoundingBox?.citationId &&
            !isSessionComplete && (
              <TooltipProvider delayDuration={1000}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onToggleKeyCitation}
                      className={`flex items-center h-7 text-xs ${
                        compactActions ? "px-2" : "gap-2"
                      }`}
                    >
                      <Star
                        className={`h-4 w-4 ${
                          isKeyCitation ? "fill-amber-400 text-amber-400" : ""
                        }`}
                      />
                      {!compactActions &&
                        (isKeyCitation ? "Remove from WP" : "Include in WP")}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>
                      {isKeyCitation
                        ? "Remove this citation from the workpaper output"
                        : "Include this citation in the workpaper output"}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          {citationPager}
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-auto">
        {fitImageToWidth && IMAGE_FILE_TYPES.has(fileType.toLowerCase()) ? (
          <div className="px-4 pb-4">
            <img
              src={currentSelectedDocumentUrl.url}
              alt={
                documentLabel ||
                selectedDocument.fileName ||
                selectedDocument.fetchableDocument?.fileName ||
                "Document preview"
              }
              className="block h-auto w-full"
              draggable={false}
            />
          </div>
        ) : (
          <FileViewer
            allowLinking={canEdit}
            allowDeleting={canEdit}
            showTickmarks={showTickmarks}
            signedUrl={currentSelectedDocumentUrl?.url}
            fileType={fileType}
            fileName={
              selectedDocument?.fileName ||
              selectedDocument?.fetchableDocument?.fileName
            }
            initialPage={currentBoundingBox?.pageNumber}
            videoDocument={currentSelectedDocumentUrl?.videoDocument}
            videoDocumentError={currentSelectedDocumentUrl?.videoDocumentError}
            initialTimestamp={currentBoundingBox?.timestampSeconds}
            boundingBoxes={viewerBoundingBoxes}
            boundingBox={currentBoundingBox?.boundingBox}
            pageWidth={currentBoundingBox?.pageWidth}
            pageHeight={currentBoundingBox?.pageHeight}
            elementId={currentBoundingBox?.elementId}
            selectedBoundingBoxId={selectedBoundingBoxId}
            selectedBoundingBoxIndex={selectedBoundingBoxIndex}
            defaultSearchValue={currentSelectionValue}
            documentExtraction={currentSelectedDocumentUrl?.documentExtraction}
            onLinkingComplete={onLinkingComplete}
            onDelete={onDelete}
            onSelect={onSelect}
          />
        )}
      </div>
    </div>
  );
}
